import { Select } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { useGroupTypes } from "src/contexts/global/WorkspaceContext";
import TeamTypeOptionLabel from "src/pages/Workspaces/components/TagTypeConfig/TeamTypeOptionLabel";
import {
  ALL_DEMAND_GROUP_TYPES,
  ALL_DOES_NOT_PARTICIPATE_GROUP_TYPES,
  ALL_SUPPLY_GROUP_TYPES,
  DEFAULT_ALLOWED_GROUP_TYPE,
} from "src/pages/Workspaces/consts";
import { getActiveGroupTypes } from "src/util/group";
import { getSelectedOptions } from "src/util/selectUtil";

import { useAIDashboardStore } from "../context/AIDashboardContext";

const CanvGroupTypesSelect = ({ channel, label, placeholder }) => {
  const store = useAIDashboardStore(channel);
  const publish = store((state) => state.publish);
  const value = store((state) => state.value);

  const groupTypes = useGroupTypes();

  const { groupTypeOptions, groupTypeOptionsByKey } = useMemo(() => {
    const activeGroupTypes = getActiveGroupTypes(groupTypes);

    // Sort group types alphabetically by name
    const sortedGroupTypes = Object.values(activeGroupTypes).sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    const options = sortedGroupTypes.map(({ id, name }) => ({
      key: id,
      label: name,
      value: id,
    }));

    // Add filter options at the top
    options.unshift(ALL_DOES_NOT_PARTICIPATE_GROUP_TYPES);
    options.unshift(ALL_SUPPLY_GROUP_TYPES);
    options.unshift(ALL_DEMAND_GROUP_TYPES);
    options.unshift(DEFAULT_ALLOWED_GROUP_TYPE);

    // Create lookup object
    const optionsByKey = {};
    options.forEach((option) => {
      optionsByKey[option.key] = option;
    });

    return { groupTypeOptions: options, groupTypeOptionsByKey: optionsByKey };
  }, [groupTypes]);

  const formatOptionLabel = useMemo(() => {
    return (option) => (
      <TeamTypeOptionLabel option={option} groupTypes={groupTypes} />
    );
  }, [groupTypes]);

  const handleChange = (selected) => {
    // Extract keys from selected options
    const selectedKeys = selected ? selected.map((opt) => opt.key) : [];
    publish(selectedKeys);
  };

  const selectedOptions = useMemo(() => {
    if (!value || !Array.isArray(value) || value.length === 0) {
      return [];
    }
    return getSelectedOptions(groupTypeOptionsByKey, value);
  }, [value, groupTypeOptionsByKey]);

  return (
    <div>
      {label && <div style={{ marginBottom: "8px" }}>{label}</div>}
      <Select
        height="40px"
        isMulti
        placeholder={placeholder || "Select team types"}
        options={groupTypeOptions}
        value={selectedOptions}
        onChange={handleChange}
        formatOptionLabel={formatOptionLabel}
      />
    </div>
  );
};

CanvGroupTypesSelect.propTypes = {
  channel: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
};

export default CanvGroupTypesSelect;
